import { GOOGLE_METADATA_BY_KEY, SpateMetricMetadata } from 'utils/metrics';
import { clusterFilterOptions, healthStatFilterOptions } from 'utils/filters';
import {
    getSortOptionKey,
    useSortSearchParamsStateOnly,
} from 'utils/hooks/useSort';
import { useEffect, useMemo } from 'react';
import { useFilterSearchParamsStateOnly } from 'utils/hooks/useFilter';
import useGoogleRelatedEntities from 'utils/hooks/useGoogleRelatedEntities';
import useHandleSearchInputChange from 'utils/hooks/useHandleSearchInputChange';
import { useLocation } from 'react-router';
import { useRelatedSearchGroupsWithData } from 'utils/hooks/api';
import useTableColumns from 'utils/hooks/useTableColumns';
import useURLSearchParams from 'utils/hooks/useURLSearchParams';
import useStaleFilterNotice from 'utils/hooks/useStaleFilterNotice';

const SORT_KEY_TO_JSONB_KEY = {
    volume: 'Search Volume',
    absoluteGrowth: 'absolute growth',
    MoM_increase: 'monthly absolute growth',
};

const metrics = [
    [GOOGLE_METADATA_BY_KEY.volume, true],
    [GOOGLE_METADATA_BY_KEY.absoluteGrowth, true],
    [GOOGLE_METADATA_BY_KEY.YoY, true],
    [GOOGLE_METADATA_BY_KEY.QoQ_increase, false],
    [GOOGLE_METADATA_BY_KEY.QoQ, true],
    [GOOGLE_METADATA_BY_KEY.MoM_increase, false],
    [GOOGLE_METADATA_BY_KEY.MoM, false],
].map(([config, visible]) => {
    const { healthStatKey: _healthStatKey, ...restConfig } = config.config;
    if (config.key === GOOGLE_METADATA_BY_KEY.YoY.key) {
        restConfig.healthStatKey = 'growth';
        restConfig.activeHealthStatLabel = true;
    }
    return [new SpateMetricMetadata(restConfig), visible];
});

function useGoogleRelatedTableProps({
    brands,
    categories,
    countries,
    getColumnsConfig,
    handleFilter,
    initialSortOption,
    initialFilterOption,
    name,
    paginateBy,
    filterParamSeparator = ',',
    filterParamPrefix,
    sortParamKey = 'sort',
}) {
    const searchParams = useURLSearchParams();
    const initialFilterKey = useMemo(
        () => JSON.stringify(initialFilterOption || {}),
        [initialFilterOption],
    );
    const stableInitialFilterOption = useMemo(
        () => initialFilterOption,
        [initialFilterKey],
    );
    const supportedFiltersKey = Object.keys(handleFilter || {})
        .sort()
        .join(',');
    const supportedFilters = useMemo(
        () => (supportedFiltersKey ? supportedFiltersKey.split(',') : []),
        [supportedFiltersKey],
    );
    const filterParamsSignature = useMemo(
        () => searchParams.toString(),
        [searchParams],
    );
    const healthStatOptions = useMemo(
        () => healthStatFilterOptions.map(({ options }) => options).flat(),
        [],
    );
    const affinityGroupQuery = useRelatedSearchGroupsWithData({
        categories: categories || null,
        brands: brands || null,
        countries: countries || null,
    });
    const fetchedParentNameOptions = useMemo(() => {
        return (affinityGroupQuery.data || []).map(({ name, id, volume }) => ({
            label: name,
            value: id,
            volume,
        }));
    }, [affinityGroupQuery.data]);
    const fallbackParentNameOptions = useMemo(() => {
        const paramKey = filterParamPrefix
            ? `${filterParamPrefix}parentName`
            : 'parentName';
        const paramValue = searchParams.get(paramKey);
        return paramValue
            ? paramValue
                  .split(filterParamSeparator)
                  .map((v) => v.trim())
                  .filter(Boolean)
                  .map((v) => ({ label: v, value: v }))
            : [];
    }, [filterParamPrefix, filterParamSeparator, searchParams]);
    const parentNameOptions = useMemo(() => {
        const optionsByLabel = new Map(
            fetchedParentNameOptions.map((option) => [option.label, option]),
        );
        fallbackParentNameOptions.forEach((option) => {
            if (!optionsByLabel.has(option.label)) {
                optionsByLabel.set(option.label, option);
            }
        });
        return optionsByLabel.size
            ? Array.from(optionsByLabel.values())
            : fallbackParentNameOptions;
    }, [fetchedParentNameOptions, fallbackParentNameOptions]);
    const filterAccessorsAndOptions = useMemo(
        () => ({
            cluster: {
                accessor: (option) => option.value,
                options: clusterFilterOptions,
            },
            parentName: {
                accessor: (option) => option.label,
                options: parentNameOptions,
            },
            healthStats: {
                accessor: (option) => option.value,
                options: healthStatOptions,
            },
        }),
        [healthStatOptions, parentNameOptions],
    );
    const urlFilterState = useFilterSearchParamsStateOnly({
        filterAccessorsAndOptions,
        initialFilterOption: stableInitialFilterOption,
        supportedFilters: Object.keys(handleFilter || {}),
        separator: filterParamSeparator,
        paramPrefix: filterParamPrefix,
    });
    const { filters, filtersKey, setFilter, setFilters, resetFilters } =
        urlFilterState;
    useStaleFilterNotice(urlFilterState.staleFilters);
    const nameFilter =
        filters && filters.name && filters.name.length ? filters.name[0] : null;

    const handleSearchInputChange = useHandleSearchInputChange({
        nameFilter,
        setFilter,
    });
    const initialSortKey = useMemo(
        () => JSON.stringify(initialSortOption || {}),
        [initialSortOption],
    );
    const stableInitialSortOption = useMemo(
        () => initialSortOption,
        [initialSortKey],
    );
    const sortOptions = useMemo(
        () => [
            ...metrics.flatMap(([metric]) => [
                metric.getSortOption({ ascending: true }),
                metric.getSortOption({ ascending: false }),
            ]),
            { sortBy: 'parentName', ascending: true },
            { sortBy: 'parentName', ascending: false },
            { sortBy: 'price_segmentation', ascending: true },
            { sortBy: 'price_segmentation', ascending: false },
        ],
        [],
    );
    const {
        setSortOption: baseSetSortOption,
        sortOption,
        sortOptionKey,
    } = useSortSearchParamsStateOnly({
        initialSortOption: stableInitialSortOption,
        options: sortOptions,
        sortOptionKeyAccessor: getSortOptionKey,
        paramKey: sortParamKey,
    });
    const queryData = {
        paginateBy,
        categories: categories || null,
        brands: brands || null,
        countries: countries || null,
        enabled: !!((categories || brands) && countries),
    };
    const queryDataFilterAndSortConfig = {
        order_by:
            SORT_KEY_TO_JSONB_KEY[sortOption?.sortBy] || sortOption?.sortBy,
        ascending: sortOption?.ascending,
        ...(filters?.name?.length && { search: filters.name[0] }),
        ...(filters?.parentName?.length && {
            affinity_group: filters.parentName.map(({ label }) => label),
        }),
        ...(filters?.cluster?.length && {
            cluster: filters.cluster.map(({ value }) => value),
        }),
        ...(filters?.healthStats?.filter(
            ({ metricLabel }) => metricLabel === 'price point',
        )?.length && {
            price_segmentation: filters?.healthStats
                ?.filter(
                    ({ metricLabel, label }) =>
                        metricLabel === 'price point' &&
                        ['High', 'Medium', 'Low'].includes(label),
                )
                .map(({ label }) => {
                    if (label === 'High') return 'HIGH PRICE';
                    if (label === 'Medium') return 'MEDIUM PRICE';
                    if (label === 'Low') return 'LOW PRICE';
                    return null;
                }),
        }),
    };
    const { query, getQueryData } = useGoogleRelatedEntities({
        ...queryDataFilterAndSortConfig,
        ...queryData,
    });
    const {
        fetchNextPage,
        isFetchingNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetching,
        ...result
    } = query;
    const rows = (result.data?.pages || []).reduce((acc, { data }) => {
        if (!data) return acc;
        acc.push(...data.map((o) => ({ ...o, relatedName: name })));
        return acc;
    }, []);
    const isSuccess = result.isSuccess;
    const isLoading = result.isLoading || result.isFetchingNextPage;
    const dataReady = isSuccess && rows.length > 0;

    const { visibleOrderedMetrics, columnsMenu, setSortOption } =
        useTableColumns({
            tableName: `google-related-${paginateBy}`,
            metrics,
            setSortOption: baseSetSortOption,
            sortOption,
        });

    const visibleColumnsConfig = getColumnsConfig(visibleOrderedMetrics);

    const isNoData = !(rows && rows.length);
    let noDataMessage;

    if (!isNoData && isSuccess) {
        noDataMessage = `No data to display. ${
            filters && Object.keys(filters).length
                ? 'Try adjusting the filters'
                : ''
        }`;
    } else if (isNoData && isLoading) {
        noDataMessage = 'Loading...';
    } else if (isNoData && result.isError) {
        noDataMessage =
            'Could not load the data. Refresh the page to try again.';
    } else if (isNoData) {
        noDataMessage = `No ${paginateBy} for this category.`;
    }

    const { pathname } = useLocation();
    useEffect(() => {
        const hasFilterParams = supportedFilters.some((key) =>
            searchParams.get(
                filterParamPrefix ? `${filterParamPrefix}${key}` : key,
            ),
        );
        const hasInitialFilters =
            Object.keys(stableInitialFilterOption || {}).length > 0;
        if (!hasFilterParams && hasInitialFilters) {
            resetFilters();
        }
    }, [
        pathname,
        resetFilters,
        filterParamsSignature,
        supportedFiltersKey,
        stableInitialFilterOption,
        filterParamPrefix,
    ]);

    return {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData: rows,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        resetFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        pages: result.data?.pages,
        getQueryData,
    };
}

export default useGoogleRelatedTableProps;
